import { PROMOCODE_CONFIG, PromocodeHelpers } from '../config/promocode.js';

/**
 * Enhanced Promocode Redemption Logic
 * Handles all scenarios for multiple promocode redemptions intelligently
 */
class EnhancedPromocodeRedemption {
  
  /**
   * Determine the best strategy for handling multiple promocode redemptions
   * @param {Object} existingPlan - User's current plan
   * @param {Object} newPromocode - New promocode being redeemed
   * @returns {Object} Redemption strategy
   */
  static determineRedemptionStrategy(existingPlan, newPromocode) {
    const existingPlatform = existingPlan.subscription_plan;
    const existingBillingCycle = existingPlan.billing_cycle;
    const newPlatform = newPromocode.subscriptionPlan;
    const newBillingCycle = newPromocode.billingCycle;
    
    // Scenario 1: Exact match (same platform + same billing cycle)
    if (existingPlatform === newPlatform && existingBillingCycle === newBillingCycle) {
      return {
        strategy: 'MERGE_EXACT',
        action: 'Add credits and extend reset date',
        reason: 'Same platform and billing cycle'
      };
    }
    
    // Scenario 2: Lifetime plan exists - always accumulate credits
    if (existingBillingCycle === 'yearly' && existingPlan.credits_total >= 10000) {
      return {
        strategy: 'ACCUMULATE_TO_LIFETIME',
        action: 'Add credits to existing lifetime plan',
        reason: 'Lifetime plans always accumulate credits from other plans'
      };
    }
    
    // Scenario 3: New promocode is lifetime - upgrade existing plan
    if (newBillingCycle === 'yearly' && newPromocode.credits >= 10000) {
      return {
        strategy: 'UPGRADE_TO_LIFETIME',
        action: 'Upgrade existing plan to lifetime',
        reason: 'Lifetime plans have higher priority'
      };
    }
    
    // Scenario 4: Same platform, different billing cycle
    if (existingPlatform === newPlatform) {
      return {
        strategy: 'MERGE_SAME_PLATFORM',
        action: 'Add credits and extend reset date',
        reason: 'Same platform, merge credits'
      };
    }
    
    // Scenario 5: Different platform, same billing cycle
    if (existingBillingCycle === newBillingCycle) {
      return {
        strategy: 'MERGE_SAME_BILLING',
        action: 'Add credits and extend reset date',
        reason: 'Same billing cycle, merge credits'
      };
    }
    
    // Scenario 6: Completely different plans
    return {
      strategy: 'CREATE_SEPARATE',
      action: 'Create new separate plan',
      reason: 'Different platform and billing cycle'
    };
  }
  
  /**
   * Calculate new reset date based on plan type and current reset date
   * @param {string} billingCycle - Billing cycle of the plan
   * @param {string} currentResetDate - Current reset date
   * @param {number} credits - Credits amount to determine if it's lifetime
   * @returns {string} New reset date
   */
  static calculateNewResetDate(billingCycle, currentResetDate, credits = 0) {
    const currentDate = new Date(currentResetDate);
    
    if (billingCycle === 'yearly') {
      // Check if it's a lifetime plan (high credits) or regular yearly plan
      if (credits >= 10000) {
        // Lifetime plans: extend by 10 years
        currentDate.setFullYear(currentDate.getFullYear() + 10);
      } else {
        // Regular yearly plans: extend by 1 year
        currentDate.setFullYear(currentDate.getFullYear() + 1);
      }
    } else if (billingCycle === 'monthly') {
      // Monthly plans: extend by 1 month
      currentDate.setMonth(currentDate.getMonth() + 1);
    } else {
      // Default: extend by 1 year
      currentDate.setFullYear(currentDate.getFullYear() + 1);
    }
    
    return currentDate.toISOString().split('T')[0];
  }
  
  /**
   * Get user-friendly message for the redemption strategy
   * @param {Object} strategy - Redemption strategy
   * @param {Object} existingPlan - Existing plan details
   * @param {Object} newPromocode - New promocode details
   * @returns {string} User-friendly message
   */
  static getRedemptionMessage(strategy, existingPlan, newPromocode) {
    const credits = newPromocode.credits;
    const platform = newPromocode.subscriptionPlan;
    
    switch (strategy.strategy) {
      case 'MERGE_EXACT':
        return `Credits added to your existing ${platform} plan! ${credits.toLocaleString()} credits added and reset date extended.`;
      
      case 'ACCUMULATE_TO_LIFETIME':
        return `Credits added to your lifetime plan! ${credits.toLocaleString()} credits added to your existing lifetime plan.`;
      
      case 'UPGRADE_TO_LIFETIME':
        return `Plan upgraded to lifetime! Your existing plan has been upgraded to lifetime with ${credits.toLocaleString()} credits.`;
      
      case 'UPGRADE_BILLING_CYCLE':
        return `Plan upgraded! Your ${platform} plan has been upgraded from monthly to yearly with ${credits.toLocaleString()} credits.`;
      
      case 'MERGE_SAME_PLATFORM':
        return `Credits added to your ${platform} plan! ${credits.toLocaleString()} credits added and reset date extended.`;
      
      case 'MERGE_SAME_BILLING':
        return `Credits added to your plan! ${credits.toLocaleString()} credits added and reset date extended.`;
      
      case 'CREATE_SEPARATE':
        return `New ${platform} plan created! ${credits.toLocaleString()} credits added to your new plan.`;
      
      default:
        return `Promocode redeemed successfully! ${credits.toLocaleString()} credits added.`;
    }
  }
  
  /**
   * Check if a subscription plan is a paid plan (not a promocode plan)
   * @param {string} subscriptionPlan - The subscription plan name
   * @returns {boolean} True if it's a paid plan, false if it's a promocode plan
   */
  static isPaidPlan(subscriptionPlan) {
    // Paid plans have specific names: 'Pro', 'Enterprise', 'free'
    // Promocode plans have platform names: 'AppSumo', 'Partner', etc.
    const paidPlanNames = ['Pro', 'Enterprise', 'free'];
    return paidPlanNames.includes(subscriptionPlan);
  }

  /**
   * Find the best existing plan to merge with
   * @param {Array} existingPlans - All existing plans for the user
   * @param {Object} newPromocode - New promocode being redeemed
   * @returns {Object|null} Best plan to merge with, or null if should create new
   */
  static findBestPlanToMerge(existingPlans, newPromocode) {
    if (!existingPlans || existingPlans.length === 0) {
      return null;
    }
    
    // Filter out paid plans - only consider promocode plans for merging
    const promocodePlans = existingPlans.filter(plan => 
      !this.isPaidPlan(plan.subscription_plan)
    );
    
    if (promocodePlans.length === 0) {
      return null; // No promocode plans to merge with
    }
    
    // Priority 1: Check if new promocode is lifetime - upgrade existing plan
    if (newPromocode.planType === 'lifetime' || (newPromocode.billingCycle === 'yearly' && newPromocode.credits >= 10000)) {
      // Find best existing plan to upgrade to lifetime
      const bestPlan = promocodePlans.reduce((best, current) => 
        current.credits_total > best.credits_total ? current : best
      );
      return { plan: bestPlan, strategy: 'UPGRADE_TO_LIFETIME' };
    }
    
    // Priority 2: Exact match (same platform + same billing cycle)
    const exactMatch = promocodePlans.find(plan => 
      plan.subscription_plan === newPromocode.subscriptionPlan && 
      plan.billing_cycle === newPromocode.billingCycle
    );
    if (exactMatch) {
      return { plan: exactMatch, strategy: 'MERGE_EXACT' };
    }
    
    // Priority 3: Lifetime plan exists - always accumulate to lifetime
    const lifetimePlan = promocodePlans.find(plan => 
      plan.billing_cycle === 'yearly' && plan.credits_total >= 10000
    );
    if (lifetimePlan) {
      return { plan: lifetimePlan, strategy: 'ACCUMULATE_TO_LIFETIME' };
    }
    
    // Priority 4: Same platform, different billing cycle - upgrade billing cycle
    const samePlatform = promocodePlans.find(plan => 
      plan.subscription_plan === newPromocode.subscriptionPlan
    );
    if (samePlatform) {
      // Check if we're upgrading billing cycle (monthly -> yearly, yearly -> lifetime)
      if (samePlatform.billing_cycle === 'monthly' && newPromocode.billingCycle === 'yearly') {
        return { plan: samePlatform, strategy: 'UPGRADE_BILLING_CYCLE' };
      } else if (samePlatform.billing_cycle === 'yearly' && newPromocode.billingCycle === 'yearly' && newPromocode.credits >= 10000) {
        return { plan: samePlatform, strategy: 'UPGRADE_TO_LIFETIME' };
      } else {
        return { plan: samePlatform, strategy: 'MERGE_SAME_PLATFORM' };
      }
    }
    
    // Priority 5: New promocode is lifetime - upgrade best existing promocode plan
    if (newPromocode.billingCycle === 'yearly' && newPromocode.credits >= 10000) {
      // Check if this is actually a lifetime plan (high credits + yearly)
      // For now, treat any yearly plan with 10K+ credits as lifetime
      const bestPlan = promocodePlans.reduce((best, current) => 
        current.credits_total > best.credits_total ? current : best
      );
      return { plan: bestPlan, strategy: 'UPGRADE_TO_LIFETIME' };
    }
    
    // Priority 6: Same billing cycle, different platform
    const sameBilling = promocodePlans.find(plan => 
      plan.billing_cycle === newPromocode.billingCycle
    );
    if (sameBilling) {
      return { plan: sameBilling, strategy: 'MERGE_SAME_BILLING' };
    }
    
    // No suitable plan found - create new
    return null;
  }
}

export default EnhancedPromocodeRedemption;
